/* eslint-disable jsx-a11y/img-redundant-alt */
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import useViatorApi, {
  defaultBookingConfirmRequest,
} from "../../../hooks/useViatorApi";
import {
  Button,
  Modal,
  PreLoading,
  SvgIcons,
  RatingStars,
} from "@tripian/react";
import ImageGallery from "../../../components/ImageGallery/ImageGallery";
import ImageSlider from "../../../components/ImageSlider/ImageSlider";
import * as gtag from "../../../gtag";

import { useNavigate } from "react-router-dom";
import useTranslate from "../../../hooks/useTranslate";
import Model, { helper, Providers } from "@tripian/model";
import useUser from "../../../hooks/useUser";
import Checkout from "./Checkout/Checkout";
import ViatorBookingDetails from "./ViatorBookingDetails/ViatorBookingDetails";
import { useDispatch } from "react-redux";
import { saveNotification } from "../../../redux/action/user";
import ViatorTourInfoForm from "./ViatorTourInfoForm/ViatorTourInfoForm";
import ViatorTourOption from "./ViatorTourOption/ViatorTourOption";
import isoLanguages from "./Checkout/ViatorBookingActivityDetails/isoLanguages";
import {
  LOCAL_EXPERIENCES,
  TOURS_AND_TICKETS,
  TRIPS,
} from "../../../constants/ROUTER_PATH_TITLE";
import useCities from "../../../hooks/useCities";
import classes from "./VistorTourInfo.module.scss";

interface IVistorTourInfo {
  tourId: string;
  cityId: number;
  lat?: number;
  lng?: number;
  cityName?: string;
  startDate?: string;
  endDate?: string;
  adults?: number;
  children?: number;
  previousRoute?: string;
  flags?: string[];
  isOpen: boolean;
  onClose: () => void;
}

const VistorTourInfo: React.FC<IVistorTourInfo> = ({
  tourId,
  cityId,
  lat,
  lng,
  cityName,
  startDate,
  endDate,
  adults,
  children,
  previousRoute,
  flags,
  isOpen,
  onClose,
}) => {
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const leftRef = useRef<HTMLDivElement>(null);
  const [leftHeight, setLeftHeight] = useState<number | undefined>(undefined);
  const formRef = useRef<HTMLDivElement>(null);
  const [formsHeight, setFormsHeight] = useState<number | undefined>(undefined);
  const availabilityRef = useRef<HTMLDivElement>(null);

  const {
    fetchProductInfo,
    viatorProductInfo,
    fetchProductAvailability,
    viatorProductAvailability,
    exchangeRates,
    successfullBooking,
    fetchProductBookingHold,
    paymentSessionToken,
    loadings,
    bookingConfirmRequest,
    setBookingConfirm,
    setBookingConfirmLoading,
    showInfoModal,
    setBookingInfoModal,
    confirmPayment,
    viatorBookingInfo,
    selectedHour,
    changeSelectedHour,
    availableDate,
    changeAvailableDate,
    locations,
    getCancellationPolicy,
    modalVisible,
    paymentFailed,
    setPaymentFailedModal,
  } = useViatorApi(Number(cityId), cityName, startDate, endDate);

  const [personsCategories, setPersonsCategories] = useState<
    (Providers.Viator.AgeBand & { count: number })[]
  >([]);
  const [showCheckout, setShowCheckout] = useState(false);
  const [selectedTourOptionCode, setSelectedTourOptionCode] =
    useState<string>();
  const [isAvailabilityChecked, setIsAvailabilityChecked] =
    useState<boolean>(false);

  const { user } = useUser();
  const navigate = useNavigate();
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const { cities } = useCities();

  // Get city coordinates for fallback
  const cityCoordinate = useMemo(() => {
    const city = cities?.find(c => c.id === cityId);
    return city?.coordinate ? { lat: city.coordinate.lat, lng: city.coordinate.lng } : undefined;
  }, [cities, cityId]);

  // Close checkout when confirmation modal shows
  useEffect(() => {
    if (showInfoModal) {
      setShowCheckout(false);
    }
  }, [showInfoModal]);

  useEffect(() => {
    if (isOpen) {
      fetchProductInfo(tourId);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen, fetchProductInfo, tourId]);

  useEffect(() => {
    if (leftRef.current) {
      setLeftHeight(leftRef.current.offsetHeight);
    }
    if (formRef.current) {
      setFormsHeight(formRef.current.offsetHeight);
    }
  }, [viatorProductInfo, loadings, isGalleryOpen]);

  useEffect(() => {
    if (viatorProductInfo) {
      gtag.googleAnalyticsDimension(
        "viewedProductUrl",
        viatorProductInfo.info.productUrl
      );
    }
  }, [viatorProductInfo]);

  useEffect(() => {
    if (successfullBooking) {
      gtag.googleAnalyticsEvent("booking_process_payment_success", { provider: "viator" });
    }
  }, [successfullBooking]);

  useEffect(() => {
    if (paymentFailed) {
      gtag.googleAnalyticsEvent("booking_process_payment_fail", { provider: "viator" });
    }
  }, [paymentFailed]);


  useEffect(() => {
    if (
      viatorProductInfo &&
      viatorProductInfo.info.pricingInfo.ageBands.length > 0
    ) {
      const newPersonsCategories =
        viatorProductInfo.info.pricingInfo.ageBands.map(x => ({
          ...x,
          count: 0,
        }));

      const adultsCount = adults ? Number(adults) : 1;
      const childrenCount = children ? Number(children) : 0;

      // ADULT
      const newAdultsCategories = newPersonsCategories.find(
        x => x.ageBand === "ADULT"
      );
      if (newAdultsCategories) {
        newAdultsCategories.count = adultsCount;
      }

      // CHILD
      const newChildrenCategories = newPersonsCategories.find(
        x => x.ageBand === "CHILD"
      );
      if (newChildrenCategories) {
        newChildrenCategories.count = childrenCount;
      }

      // TRAVELER
      const travelerCategory = newPersonsCategories.find(
        x => x.ageBand === "TRAVELER"
      );
      if (travelerCategory) {
        travelerCategory.count = adultsCount + childrenCount;
      }

      setPersonsCategories(newPersonsCategories);
    }
  }, [adults, children, viatorProductInfo]);

  const cancellationInfo = useMemo(() => {
    const cancellationTime = new Date();
    if (viatorProductInfo) {
      return getCancellationPolicy(
        viatorProductInfo?.info.cancellationPolicy,
        cancellationTime
      );
    }
    return undefined;
  }, [getCancellationPolicy, viatorProductInfo]);

  const pickupIncluded = useMemo(() => {
    if (!viatorProductInfo) return false;

    if (
      viatorProductInfo.info?.logistics?.travelerPickup?.pickupOptionType ===
      "PICKUP_EVERYONE"
    ) {
      return true;
    } else if (
      viatorProductInfo.info?.logistics?.travelerPickup?.pickupOptionType ===
      "PICKUP_AND_MEET_AT_START_POINT"
    ) {
      const pickupDesc = viatorProductInfo.info?.productOptions?.find(
        o => o.productOptionCode === selectedTourOptionCode
      )?.description;
      if (pickupDesc) {
        const isIncluded = pickupDesc.includes("Pickup included");
        return isIncluded;
      }
      return false;
    }
    return false;
  }, [selectedTourOptionCode, viatorProductInfo]);

  const duration = useMemo(() => {
    if (!viatorProductInfo) return null;

    if (viatorProductInfo.info.itinerary.duration.fixedDurationInMinutes) {
      return (
        <span className={classes.numberOfRatings}>
          {viatorProductInfo.info.itinerary.duration.fixedDurationInMinutes}{" "}
          {t("trips.myTrips.localExperiences.tourDetails.mins")}
        </span>
      );
    }
    if (
      viatorProductInfo.info.itinerary.duration.variableDurationFromMinutes &&
      viatorProductInfo.info.itinerary.duration.variableDurationToMinutes
    ) {
      return (
        <span className={classes.numberOfRatings}>
          {
            viatorProductInfo.info.itinerary.duration
              .variableDurationFromMinutes
          }{" "}
          -{" "}
          {viatorProductInfo.info.itinerary.duration.variableDurationToMinutes}
          {t("trips.myTrips.localExperiences.tourDetails.mins")}
        </span>
      );
    }
    return null;
  }, [viatorProductInfo, t]);

  const groupedBookableItems = useMemo(() => {
    if (!viatorProductAvailability?.bookableItems) return [];

    const grouped = viatorProductAvailability.bookableItems.reduce(
      (acc, item) => {
        if (!item) return acc;

        const {
          productOptionCode,
          startTime,
          available,
          unavailableReason,
          totalPrice,
          lineItems,
        } = item;

        if (!productOptionCode) return acc;

        // Initialize group if not already present
        if (!acc[productOptionCode]) {
          acc[productOptionCode] = {
            productOptionCode,
            timedEntries: [],
            available: false,
            totalPrice: totalPrice || {},
            lineItems: lineItems || [],
          };
        }

        // Add timed entry for this startTime if defined
        if (startTime || available !== undefined || unavailableReason) {
          acc[productOptionCode].timedEntries.push({
            startTime,
            available: !!available,
            unavailableReason,
          });
        }

        // Update `available` to true if any entry is available
        if (available) {
          acc[productOptionCode].available = true;
        }

        // Update lineItems and totalPrice (assuming they are consistent across grouped items)
        if (lineItems) {
          acc[productOptionCode].lineItems = lineItems;
        }
        if (totalPrice) {
          acc[productOptionCode].totalPrice = totalPrice;
        }

        return acc;
      },
      {} as Record<
        string,
        {
          productOptionCode: string;
          timedEntries: {
            startTime?: string;
            available: boolean;
            unavailableReason?: string;
          }[];
          available: boolean;
          totalPrice: Providers.Viator.TotalPrice;
          lineItems: Providers.Viator.LineItem[];
        }
      >
    );

    return Object.values(grouped);
  }, [viatorProductAvailability?.bookableItems]);

  const openGallery = (index: number) => {
    setGalleryIndex(index);
    setIsGalleryOpen(true);
  };

  const closeGallery = () => {
    setIsGalleryOpen(false);
  };

  const nextGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev => (prev + 1) % (viatorProductInfo?.info.images?.length ?? 1)
    );
  }, [viatorProductInfo?.info.images?.length]);

  const prevGalleryImage = useCallback(() => {
    setGalleryIndex(
      prev =>
        (prev - 1 + (viatorProductInfo?.info.images?.length ?? 1)) %
        (viatorProductInfo?.info.images?.length ?? 1)
    );
  }, [viatorProductInfo?.info.images?.length]);

  const onHoldBooking = async (productOptionCode: string) => {
    gtag.googleAnalyticsEvent("tour_book_click", { provider: "viator", tour_id: tourId });
    const viatorConfig = window.tconfig.PROVIDERS.tourAndTicket.find(x => x.id === Model.PROVIDER_ID.VIATOR);
    const campaignId = viatorConfig?.clientId || "";
    const request: Providers.Viator.BookingCardHoldRequest = {
      currency: "USD",
      partnerCartRef: `partnerCartRef${Math.random().toString(36).substring(4)}`,
      items: [
        {
          partnerBookingRef: campaignId ? `${campaignId}_${Date.now()}` : `partnerBookingRef${Math.random().toString(36).substring(4)}`,
          productCode: viatorProductInfo?.availability.productCode || "",
          productOptionCode: productOptionCode,
          startTime: selectedHour !== "" ? selectedHour : undefined,
          travelDate: availableDate,
          paxMix: personsCategories
            .filter(item => item.count > 0)
            .map(item => ({
              ageBand: item.ageBand,
              numberOfTravelers: item.count,
            })),
        },
      ],
      paymentDataSubmissionMode: "VIATOR_FORM",
      hostingUrl: window.location.origin,
    };
    const bookingHold = await fetchProductBookingHold(request);

    if (bookingHold === true) {
      setShowCheckout(true);
    } else {
      setShowCheckout(false);
    }
  };

  const onConfirmBooking = async (paymentToken: string) => {
    const bookingConfirm = {
      ...bookingConfirmRequest,
      paymentToken,
    };
    const confirmed = await confirmPayment(bookingConfirm);
    if (confirmed) setBookingConfirm(defaultBookingConfirmRequest);
    return confirmed;
  };

  const forms = (viatorTourDataParam: Providers.Viator.TourData) => {
    if (successfullBooking) {
      gtag.googleAnalyticsDimension(
        "bookedProductUrl",
        viatorTourDataParam.info.productUrl
      );
      return null;
    } else {
      return (
        <>
          <ViatorTourInfoForm
            bookableItems={viatorTourDataParam.availability.bookableItems || []}
            productCode={viatorTourDataParam.availability.productCode}
            productOptionCode={
              selectedTourOptionCode ||
              viatorProductInfo?.availability.bookableItems?.[0]
                ?.productOptionCode
            }
            productURL={viatorTourDataParam.info.productUrl}
            availableDate={availableDate}
            setAvailableDate={changeAvailableDate}
            formPersonsCategories={
              viatorTourDataParam.info.pricingInfo.ageBands
            }
            personsCategories={personsCategories}
            setPersonsCategories={setPersonsCategories}
            onScrollToAvailability={() => {
              setTimeout(() => {
                availabilityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
            }}
            checkAvailabilityClick={async (
              availabilityRequest: Providers.Viator.AvailabilityCheckRequest
            ) => {
              setTimeout(() => {
                availabilityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
              }, 0);
              const result =
                await fetchProductAvailability(availabilityRequest);
              setIsAvailabilityChecked(true);
              return result;
            }}
            bookingRequirements={viatorTourDataParam.info.bookingRequirements}
            timeZone={viatorProductInfo?.info.timeZone}
            t={t}
          />
        </>
      );
    }
  };

  if (!isOpen) return null;

  // Show loading state
  if (
    loadings === "loadingViatorProductInfo" ||
    loadings === "loadingBookingConfirm" ||
    viatorProductInfo === undefined
  ) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[1100] overflow-y-auto">
        <div className="min-h-screen bg-white relative mt-[60px] md:mt-[90px]">
          <div className="container mx-auto px-4 py-12 relative">
            <div className="flex items-center justify-center min-h-[50vh]">
              <PreLoading />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (viatorProductInfo === undefined) {
    return (
      <>
        {/* <AppNav header={TOUR_INFO.HEADER?.(t("tourInfo.header"))} /> */}
        <div className="mt-12 mx-auto p-4 text-center">
          {t(
            "trips.myTrips.localExperiences.tourDetails.experience.doesNotSupport"
          )}{" "}
          "Viator"{" "}
          {t("trips.myTrips.localExperiences.tourDetails.experience.tours")}.
        </div>
      </>
    );
  }

  const travelPrice = () => {
    if (!viatorProductAvailability?.bookableItems || !selectedTourOptionCode) {
      return undefined;
    }

    const price = viatorProductAvailability.bookableItems.find(
      b => b.productOptionCode === selectedTourOptionCode
    )?.totalPrice.price.recommendedRetailPrice;

    return exchangeRates.length > 0 && price
      ? Number((exchangeRates[0].rate * price).toFixed(1))
      : price || 0;
  };

  const images = viatorProductInfo.info.images.map(
    image =>
      image.variants.reduce(
        (max, v) => (Number(v.width) > Number(max.width) ? v : max),
        image.variants[0]
      ).url
  );

  return (
    <>
      {showCheckout ? (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-70 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center relative">
            <div className="bg-white w-full min-h-screen">
              <Checkout
                user={user}
                tourName={viatorProductInfo.info.title}
                tourImage={
                  viatorProductInfo.info.images[0].variants[
                    viatorProductInfo.info.images[0].variants.length - 1
                  ].url
                }
                travelHour={selectedHour}
                travelDate={availableDate}
                travelPrice={travelPrice()}
                bookerInfo={bookingConfirmRequest.bookerInfo}
                communication={bookingConfirmRequest.communication}
                bookingQuestions={viatorProductInfo?.bookingQuestions}
                bookingQuestionAnswers={
                  bookingConfirmRequest.items.find(
                    item => item.bookingQuestionAnswers
                  )?.bookingQuestionAnswers
                }
                languageGuides={
                  viatorProductInfo.info.productOptions.find(
                    o => o.productOptionCode === selectedTourOptionCode
                  )?.languageGuides
                }
                logistics={viatorProductInfo.info.logistics}
                paymentSessionToken={paymentSessionToken}
                locations={locations}
                clickedBookingContactDetails={({
                  bookerInfo,
                  communication,
                }) => {
                  const bookingConfirm = {
                    ...bookingConfirmRequest,
                    communication,
                    bookerInfo,
                  };
                  setBookingConfirm(bookingConfirm);
                }}
                clickedBookingActivityDetails={({
                  bookingQuestionAnswers,
                  languageGuide,
                }) => {
                  const updatedItems = bookingConfirmRequest.items.map(item => {
                    return {
                      ...item,
                      bookingQuestionAnswers:
                        bookingQuestionAnswers || item.bookingQuestionAnswers,
                      languageGuide: languageGuide || item.languageGuide,
                    };
                  });

                  const updatedBookingConfirmRequest = {
                    ...bookingConfirmRequest,
                    items: updatedItems,
                  };

                  setBookingConfirm(updatedBookingConfirmRequest);
                }}
                setPaymentLoading={(loading: boolean) =>
                  setBookingConfirmLoading(loading)
                }
                onPaymentSuccess={(paymentToken: string) =>
                  onConfirmBooking(paymentToken)
                }
                onPaymentError={(error: string) => {
                  dispatch(
                    saveNotification(
                      Model.NOTIFICATION_TYPE.ERROR,
                      "ViatorFetchPaymentError",
                      "Submitted card details were invalid or unsupported for this purchase"
                    )
                  );
                }}
                travelerCount={personsCategories.reduce(
                  (total, traveler) => total + traveler.count,
                  0
                )}
                cancellationInfo={cancellationInfo}
                flags={flags}
                pickupIncluded={pickupIncluded}
                modalVisible={modalVisible}
                handleFetchBookingHold={() =>
                  selectedTourOptionCode &&
                  onHoldBooking(selectedTourOptionCode)
                }
                t={t}
                onClose={() => setShowCheckout(false)}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          {isGalleryOpen && viatorProductInfo && (
            <div className="fixed inset-0 z-[9999] bg-black flex items-center justify-center">
              <button
                onClick={closeGallery}
                className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
              >
                <SvgIcons.X width="32" height="32" />
              </button>
              <ImageGallery
                images={images}
                isOpen={isGalleryOpen}
                currentIndex={galleryIndex}
                onClose={closeGallery}
                onChangeIndex={setGalleryIndex}
                onNext={nextGalleryImage}
                onPrev={prevGalleryImage}
              />
            </div>
          )}
          <div className="fixed inset-0 bg-gray-50 z-[1100] overflow-y-auto">
            {/* Sticky header */}
            <div className="sticky top-[60px] md:top-[90px] z-10 bg-gray-50 border-b border-gray-100">
              <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-4">
                <button
                  onClick={onClose}
                  className="flex items-center justify-center w-9 h-9 rounded-full border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition-colors flex-shrink-0"
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M19 12H5M12 5l-7 7 7 7"/>
                  </svg>
                </button>
                {viatorProductInfo && (
                  <span className="text-sm font-medium text-gray-700 truncate">
                    {viatorProductInfo.info.title}
                  </span>
                )}
              </div>
            </div>

            {loadings.includes("loadingViatorProductInfo") ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <PreLoading />
              </div>
            ) : viatorProductInfo === undefined ? (
              <div className="flex items-center justify-center min-h-[60vh]">
                <div className="text-center p-8">
                  <p className="text-gray-500 mb-4">Your platform does not support "Viator" tours.</p>
                  <Button color="primary" text="Close" onClick={onClose} />
                </div>

              </div>
            ) : (
              <div className="max-w-6xl mx-auto px-6 pt-10 pb-16">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] mt-[60px] gap-10 items-start">

                  {/* ── Left column ─────────────────────────────────── */}
                  <div className="space-y-8 min-w-0">

                    {/* Image grid — Airbnb style */}
                    <div className="relative rounded-3xl overflow-hidden bg-gray-200" style={{ height: "420px" }}>
                      {images.length >= 3 ? (
                        <div className="grid grid-cols-2 gap-1 h-full">
                          {/* Big left image */}
                          <div
                            className="relative cursor-pointer overflow-hidden"
                            onClick={() => openGallery(0)}
                          >
                            <img
                              src={images[0]}
                              alt={viatorProductInfo.info.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                          {/* Right 2-stack */}
                          <div className="grid grid-rows-2 gap-1 h-full">
                            <div
                              className="relative cursor-pointer overflow-hidden"
                              onClick={() => openGallery(1)}
                            >
                              <img
                                src={images[1]}
                                alt=""
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div
                              className="relative cursor-pointer overflow-hidden"
                              onClick={() => openGallery(2)}
                            >
                              <img
                                src={images[2]}
                                alt=""
                                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                              />
                              {images.length > 3 && (
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                  <span className="text-white text-sm font-semibold">
                                    +{images.length - 3} photos
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="w-full h-full cursor-pointer"
                          onClick={() => openGallery(0)}
                        >
                          <img
                            src={images[0]}
                            alt={viatorProductInfo.info.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      {/* Show all photos button */}
                      <button
                        onClick={() => openGallery(0)}
                        className="absolute bottom-4 right-4 bg-white text-gray-800 text-xs font-semibold px-4 py-2 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center gap-1.5"
                      >
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/>
                        </svg>
                        Show all {images.length} photos
                      </button>
                    </div>

                    {/* Title + Rating */}
                    <div className="space-y-3">
                      <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 leading-tight">
                        {viatorProductInfo.info.title}
                      </h1>
                      {viatorProductInfo.info.reviews?.totalReviews > 0 && (
                        <div className="flex items-center gap-2">
                          <RatingStars
                            rating={(viatorProductInfo.info.reviews.combinedAverageRating * 20).toString()}
                          />
                          <span className="font-semibold text-gray-900 text-sm">
                            {viatorProductInfo.info.reviews.combinedAverageRating.toFixed(1)}
                          </span>
                          <span className="text-gray-400">·</span>
                          <span className="text-sm text-gray-600 underline cursor-pointer">
                            {viatorProductInfo.info.reviews.totalReviews}{" "}
                            {t("trips.myTrips.localExperiences.tourDetails.reviews")}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Quick info chips */}
                    <div className="flex flex-wrap gap-2">
                      {[
                        { icon: <SvgIcons.Clock2 width="16" height="16" />, label: duration },
                        {
                          icon: <SvgIcons.Car />,
                          label: (() => {
                            const p = viatorProductInfo.info?.logistics?.travelerPickup?.pickupOptionType;
                            if (!p) return null;
                            return p.replace(/_/g, " ").toLowerCase().replace(/(^|\s)\S/g, l => l.toUpperCase());
                          })(),
                        },
                        {
                          icon: <SvgIcons.MobileTicket width="16" height="16" />,
                          label: viatorProductInfo.info.ticketInfo.ticketTypes?.length > 0
                            ? viatorProductInfo.info.ticketInfo.ticketTypes.map(t => t === "MOBILE_ONLY" ? "Mobile Ticket" : t).join(", ")
                            : null,
                        },
                        {
                          icon: <SvgIcons.Offered width="16" height="16" />,
                          label: `${isoLanguages.find(l => l.code === viatorProductInfo.info.language)?.name || "English"}`,
                        },
                      ]
                        .filter(item => item.label)
                        .map((item, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-sm text-gray-700 shadow-sm"
                          >
                            <span className="flex-shrink-0 text-gray-400 flex items-center">{item.icon}</span>
                            <span>{item.label}</span>
                          </div>
                        ))}
                    </div>

                    <hr className="border-gray-200" />

                    {/* Mobile booking form */}
                    <div className="lg:hidden">
                      {forms(viatorProductInfo)}
                    </div>

                    {/* Availability options */}
                    <div ref={availabilityRef} className="scroll-mt-40">
                    {loadings === "loadingViatorProductAvailability" ? (
                      <div className="flex items-center justify-center py-12">
                        <PreLoading />
                      </div>
                    ) : (
                      <>
                        {(viatorProductAvailability?.bookableItems?.length ?? 0) > 0 && (
                          <div className="space-y-3">
                            {groupedBookableItems.map(group => (
                              <div key={group.productOptionCode} className="overflow-hidden">
                                <ViatorTourOption
                                  tourOption={group}
                                  productOption={viatorProductInfo?.info.productOptions.find(
                                    o => o.productOptionCode === group.productOptionCode
                                  )}
                                  exchangeRates={exchangeRates}
                                  travelDate={viatorProductAvailability?.travelDate || ""}
                                  selectedHour={selectedHour}
                                  setSelectedHour={changeSelectedHour}
                                  currency={viatorProductAvailability?.currency || "USD"}
                                  bookingRequestCallback={(productOptionCode: string) => {
                                    onHoldBooking(productOptionCode);
                                  }}
                                  setSelectedOption={(productOptionCode: string) => {
                                    setSelectedTourOptionCode(productOptionCode);
                                  }}
                                  isSelected={group.productOptionCode === selectedTourOptionCode}
                                  loading={loadings === "loadingBookingHold"}
                                  t={t}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                        {isAvailabilityChecked &&
                          (!viatorProductAvailability?.bookableItems?.length) && (
                          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
                            <p className="text-amber-800 font-medium">
                              {t("trips.myTrips.localExperiences.tourDetails.noAvailableDays")}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                    </div>

                    <hr className="border-gray-200" />

                    {/* About */}
                    <div>
                      <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">
                        {t("trips.myTrips.localExperiences.tourDetails.aboutThisActivity.title")}
                      </h2>
                      <div
                        className="text-gray-600 leading-relaxed text-[15px] [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5"
                        dangerouslySetInnerHTML={{ __html: viatorProductInfo.info.description }}
                      />
                    </div>

                    <hr className="border-gray-200" />

                    {/* What's included — visible on mobile */}
                    <div className="lg:hidden space-y-6">
                      {(viatorProductInfo?.info?.inclusions?.length > 0 ||
                        viatorProductInfo?.info?.exclusions?.length > 0) && (
                        <div>
                          <h2 className="text-lg lg:text-xl font-semibold text-gray-900 mb-3 lg:mb-4">
                            {t("trips.myTrips.localExperiences.tourDetails.experience.includes")}
                          </h2>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {viatorProductInfo.info.inclusions?.map((inc, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                                <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                                <span>{inc.description || inc.otherDescription || inc.typeDescription}</span>
                              </div>
                            ))}
                            {viatorProductInfo.info.exclusions?.map((exc, i) => (
                              <div key={i} className="flex items-start gap-2 text-sm text-gray-500">
                                <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                                <span>{exc.description || exc.otherDescription || exc.typeDescription}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>

                  {/* ── Right column ────────────────────────────────── */}
                  <div className="hidden lg:flex flex-col gap-5 self-start">

                    {/* Booking widget — sticky */}
                    <div className="sticky top-[150px] bg-white rounded-3xl shadow-xl border border-gray-100">
                      {forms(viatorProductInfo)}
                    </div>

                    {/* What's included */}
                    {(viatorProductInfo?.info?.inclusions?.length > 0 ||
                      viatorProductInfo?.info?.exclusions?.length > 0) && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          {t("trips.myTrips.localExperiences.tourDetails.experience.includes")}
                        </h3>
                        <div className="space-y-2">
                          {viatorProductInfo.info.inclusions?.map((inc, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <SvgIcons.Check2 fill="#16a34a" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                              <span>{inc.description || inc.otherDescription || inc.typeDescription}</span>
                            </div>
                          ))}
                          {viatorProductInfo.info.exclusions?.map((exc, i) => (
                            <div key={i} className="flex items-start gap-2 text-sm text-gray-500">
                              <SvgIcons.X fill="#9ca3af" width="16" height="16" className="flex-shrink-0 mt-0.5" />
                              <span>{exc.description || exc.otherDescription || exc.typeDescription}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cancellation */}
                    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                      <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                        {t("trips.myTrips.localExperiences.tourDetails.aboutThisActivity.cancelation")}
                      </h3>
                      <div className="text-sm text-gray-600 leading-relaxed [&_ul]:list-disc [&_ul]:pl-4 [&_li+li]:mt-1">
                        {viatorProductInfo.info.cancellationPolicy.description &&
                          (/<[a-z][\s\S]*>/i.test(viatorProductInfo.info.cancellationPolicy.description) ? (
                            <div dangerouslySetInnerHTML={{ __html: viatorProductInfo.info.cancellationPolicy.description }} />
                          ) : (
                            <p>{viatorProductInfo.info.cancellationPolicy.description}</p>
                          ))}
                      </div>
                    </div>

                    {/* Additional info */}
                    {viatorProductInfo.info.additionalInfo?.length > 0 && (
                      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                        <h3 className="font-semibold text-gray-900 mb-3 text-sm uppercase tracking-wide">
                          {t("trips.myTrips.localExperiences.tourDetails.experience.importantInformation")}
                        </h3>
                        <ul className="space-y-1.5">
                          {viatorProductInfo.info.additionalInfo.map((info, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                              <span className="flex-shrink-0 text-gray-400 mt-0.5">›</span>
                              <span>{info.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  </div>

                </div>
              </div>
            )}
          </div>
        </>
      )}

      {(showInfoModal) && (
        <Modal
          show={showInfoModal}
          className="!min-w-[90%] !w-[90%] bg-background-color md:!min-w-[50%] md:!w-32 shadow-2xl"
        >
          <div className="row m0 p-4">
            <ViatorBookingDetails
              items={viatorBookingInfo?.items}
              totalConfirmedPrice={viatorBookingInfo?.totalConfirmedPrice}
              voucherInfo={viatorBookingInfo?.voucherInfo}
              tourName={viatorProductInfo.info.title}
              tourImage={
                viatorProductInfo.info.images[0].variants[
                  viatorProductInfo.info.images[0].variants.length - 1
                ].url
              }
              travelHour={selectedHour}
              travelDate={availableDate}
              buttonText={t("trips.myTrips.localExperiences.tourDetails.close")}
              clicked={() => {
                setBookingInfoModal(false);
                if (previousRoute) {
                  if (previousRoute === TOURS_AND_TICKETS.PATH) {
                    navigate(
                      `${LOCAL_EXPERIENCES.PATH}?city_id=${cityId}&city_name=${cityName}&lat=${lat}&lng=${lng}&start_date=${startDate}&end_date=${endDate}&adult=${adults}${
                        children ? `&children=${children}` : ""
                      }`
                    );
                  } else {
                    navigate(previousRoute);
                  }
                } else {
                  navigate(TRIPS.PATH);
                }
              }}
              t={t}
              cityId={cityId}
              startDate={startDate}
              endDate={endDate}
              adults={personsCategories.find(p => p.ageBand === "ADULT")?.count || 0}
              children={personsCategories.find(p => p.ageBand === "CHILD")?.count || 0}
              duration={
                viatorProductInfo.info.itinerary.duration.fixedDurationInMinutes ||
                viatorProductInfo.info.itinerary.duration.variableDurationFromMinutes ||
                120 // mock: 2 saat
              }
              tourCoordinate={lat && lng ? { lat, lng } : cityCoordinate}
              accommodationLat={lat}
              accommodationLng={lng}
            />
          </div>
        </Modal>
      )}

      <Modal
        show={paymentFailed}
        className="booking-modal p5 min-w-[90%] md:min-w-0"
      >
        <div className="row m0">
          <div className="flex w-full text-center flex-col mb-8 font-bold text-xl mr-1">
            {t("trips.myTrips.localExperiences.tourDetails.paymentFailed")}
          </div>
          <div className="w-full flex justify-evenly">
            <Button
              color="primary"
              text={t("trips.myTrips.itinerary.offers.payment.goBack")}
              onClick={() => {
                setPaymentFailedModal(false);
                if (previousRoute) {
                  if (previousRoute === TOURS_AND_TICKETS.PATH) {
                    navigate(
                      `${LOCAL_EXPERIENCES.PATH}?city_id=${cityId}&city_name=${cityName}&start_date=${startDate}&end_date=${endDate}&adult=${adults}${
                        children ? `&children=${children}` : ""
                      }`
                    );
                  } else {
                    navigate(previousRoute);
                  }
                } else {
                  navigate(TRIPS.PATH);
                }
              }}
            />

            <Button
              color="secondary"
              text={t("trips.myTrips.localExperiences.tourDetails.tryAgain")}
              onClick={() => {
                setPaymentFailedModal(false);
                selectedTourOptionCode && onHoldBooking(selectedTourOptionCode);
              }}
            />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default VistorTourInfo;
